import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

import { cn } from './cn';

export const buttonVariants = cva(
  [
    'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-auditor font-medium no-underline transition-colors',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand text-ink-inverse hover:bg-brand-hover active:bg-brand-active',
        secondary: 'bg-brand-subtle text-brand hover:bg-brand-subtle/70 active:bg-brand-subtle/50',
        outline: 'border border-line-strong bg-surface text-ink hover:bg-canvas active:bg-brand-subtle',
        ghost: 'bg-transparent text-ink-muted hover:bg-brand-subtle hover:text-brand active:bg-brand-subtle/70',
        danger: 'bg-critical text-ink-inverse hover:bg-critical/90 active:bg-critical/80',
        link: 'text-brand underline-offset-4 hover:underline!',
      },
      size: {
        small: 'h-8 gap-1.5 px-3 text-xs',
        medium: 'h-9 gap-2 px-3.5 text-sm',
        large: 'h-11 gap-2 px-5 text-sm',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      size: 'medium',
      variant: 'primary',
    },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      children,
      className,
      disabled,
      endIcon,
      loading = false,
      loadingLabel,
      size,
      startIcon,
      type = 'button',
      variant,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : 'button';

    return (
      <Component
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(buttonVariants({ className, size, variant }))}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <LoaderCircle aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
        ) : (
          startIcon
        )}
        {/* Slottable marks which child `asChild` merges into when icons sit beside it. */}
        <Slottable>
          {loading && loadingLabel ? (
            <span className="contents">
              {children} <span className="sr-only">{loadingLabel}</span>
            </span>
          ) : (
            children
          )}
        </Slottable>
        {!loading && endIcon}
      </Component>
    );
  },
);

Button.displayName = 'Button';
